import React, { useMemo } from "react";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Skeleton from "@material-ui/lab/Skeleton";
import Rating from "@material-ui/lab/Rating";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark" || theme.palette.mode === "dark";

  return {
    tableContainer: {
      background: "transparent",
      boxShadow: "none",
    },
    headCell: {
      fontWeight: 800,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: 0.8,
      color: theme.palette.text.secondary,
      borderBottom: isDark ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(15,23,42,0.08)",
      background: isDark ? "rgba(15,23,42,0.9)" : "rgba(248,250,252,0.9)",
    },
    bodyCell: {
      borderBottom: isDark ? "1px solid rgba(148,163,184,0.1)" : "1px solid rgba(15,23,42,0.06)",
      fontWeight: 500,
    },
    row: {
      transition: "background-color 0.2s ease, transform 0.2s ease",
      "&:nth-of-type(odd)": {
        background: isDark ? "rgba(15,23,42,0.48)" : "rgba(248,250,252,0.72)",
      },
      "&:hover": {
        background: isDark ? "rgba(30,41,59,0.92)" : "rgba(239,246,255,0.8)",
      },
    },
    attendantName: {
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    ratingWrap: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
    },
    ratingValue: {
      fontSize: 13,
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    statusBadge: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 999,
      padding: "8px 12px",
      fontSize: 12,
      fontWeight: 800,
      minWidth: 84,
    },
    online: {
      background: isDark ? "rgba(34,197,94,0.18)" : "rgba(34,197,94,0.12)",
      color: "#16a34a",
    },
    offline: {
      background: isDark ? "rgba(148,163,184,0.18)" : "rgba(148,163,184,0.18)",
      color: isDark ? "#cbd5e1" : "#475569",
    },
    emptyWrap: {
      padding: theme.spacing(5),
      textAlign: "center",
      color: theme.palette.text.secondary,
    },
  };
});

export function RatingBox({ rating }) {
  const ratingTrunc = rating === null ? 0 : Math.trunc(rating || 0);

  return <Rating defaultValue={ratingTrunc} max={3} readOnly size="small" />;
}

export default function TableAttendantsStatus(props) {
  const { loading, attendants } = props;
  const classes = useStyles();

  const sortedAttendants = useMemo(
    () => [...(attendants || [])].sort((a, b) => Number(b.tickets || 0) - Number(a.tickets || 0)),
    [attendants]
  );

  function formatTime(minutes) {
    return moment().startOf("day").add(minutes || 0, "minutes").format("HH[h] mm[m]");
  }

  if (loading) {
    return <Skeleton variant="rect" height={180} />;
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table id="grid-attendants" size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.headCell}>{i18n.t("dashboard.users.name")}</TableCell>
            <TableCell align="center" className={classes.headCell}>
              {i18n.t("dashboard.assessments.score")}
            </TableCell>
            <TableCell align="center" className={classes.headCell}>
              {i18n.t("dashboard.assessments.ratedCalls")}
            </TableCell>
            <TableCell align="center" className={classes.headCell}>
              {i18n.t("dashboard.assessments.totalCalls")}
            </TableCell>
            <TableCell align="center" className={classes.headCell}>
              {i18n.t("dashboard.cards.averageWaitingTime")}
            </TableCell>
            <TableCell align="center" className={classes.headCell}>
              {i18n.t("dashboard.cards.averageServiceTime")}
            </TableCell>
            <TableCell align="center" className={classes.headCell}>
              {i18n.t("dashboard.cards.status")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedAttendants.length > 0 ? (
            sortedAttendants.map((attendant, index) => (
              <TableRow key={`${attendant.name}-${index}`} className={classes.row}>
                <TableCell className={classes.bodyCell}>
                  <Typography className={classes.attendantName}>{attendant.name}</Typography>
                </TableCell>
                <TableCell align="center" className={classes.bodyCell}>
                  <div className={classes.ratingWrap}>
                    <Typography className={classes.ratingValue}>{attendant.rating || 0}</Typography>
                    <RatingBox rating={attendant.rating} />
                  </div>
                </TableCell>
                <TableCell align="center" className={classes.bodyCell}>{attendant.countRating || 0}</TableCell>
                <TableCell align="center" className={classes.bodyCell}>{attendant.tickets || 0}</TableCell>
                <TableCell align="center" className={classes.bodyCell}>{formatTime(attendant.avgWaitTime)}</TableCell>
                <TableCell align="center" className={classes.bodyCell}>{formatTime(attendant.avgSupportTime)}</TableCell>
                <TableCell align="center" className={classes.bodyCell}>
                  <span className={`${classes.statusBadge} ${attendant.online ? classes.online : classes.offline}`}>
                    {attendant.online ? "Online" : "Offline"}
                  </span>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7}>
                <div className={classes.emptyWrap}>Nenhum atendente encontrado para o período selecionado.</div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
